import React, { useContext } from 'react';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';
import { AuthContext } from 'contexts';
import { UserRoles } from '@bb/users';
import { Icon, Card, Space, Input, Text, LogoSection } from 'components';
import { StyledGrid, StyledButton } from '../StyledComponents';
import { GeneralSettingsProps } from '../types';

const LOGO_DIMENSION = 55;
const ICON_DIMENSION = 10.95;

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  orgName,
  branchName,
  logoUrl,
  branchLogoUrl,
  title,
  openFilePicker,
}) => {
  const breakpoints = useBreakpoint();

  const { auth } = useContext(AuthContext);

  const renderLogoSection = (url?: string, logoType?: 'branch' | 'global') => (
    <EditLogoSpace>
      <LogoSectionWrapper>
        <LogoSection
          logoType={logoType}
          imageOveride={url}
          openFilePicker={openFilePicker}
        />
      </LogoSectionWrapper>
      <StyledSetting>
        <Text strong>{url ? 'Edit' : 'Add'} Logo</Text>
        <StyledButton
          data-cy="button-uploadLogo"
          size="small"
          onClick={() => openFilePicker(undefined, logoType)}
        >
          <Icon
            type="antdUploadOutlined"
            height={ICON_DIMENSION}
            width={ICON_DIMENSION}
            offset={[0, -3]}
            color="gray10"
          />
          Upload
        </StyledButton>
        <StyledButton
          data-cy="button-editLogo"
          size="small"
          onClick={() => openFilePicker(url, logoType)}
        >
          Edit
        </StyledButton>
      </StyledSetting>
    </EditLogoSpace>
  );

  return (
    <Card type="primary" title={title} id="general">
      {auth?.role && auth.role > UserRoles['Branch Admin'] && (
        <StyledGrid $breakpoints={breakpoints}>
          <div>{renderLogoSection(logoUrl, 'global')}</div>

          <StyledInput
            data-cy="input-brokerageName"
            label="Brokerage Name"
            disabled
            value={orgName}
          />
        </StyledGrid>
      )}

      {auth?.role && auth.role >= UserRoles['Branch Admin'] && (
        <StyledGrid $breakpoints={breakpoints}>
          <div>{renderLogoSection(branchLogoUrl, 'branch')}</div>

          <StyledInput
            data-cy="input-brokerageName"
            label="Branch Name"
            disabled
            value={branchName}
          />
        </StyledGrid>
      )}
    </Card>
  );
};

const LogoSectionWrapper = styled.div`
  .org-logo-circle {
    border-radius: 50%;
    height: ${LOGO_DIMENSION}px;
    width: ${LOGO_DIMENSION}px;
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.15);
    overflow: hidden;
    > img {
      width: 100%;
      height: 100%;
      padding: 6%;
      object-fit: contain;
    }
  }
`;

const EditLogoSpace = styled(Space)`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

export const StyledSetting = styled.div<{
  alignSiblings?: boolean;
  md?: boolean;
  xl?: boolean;
  xxl?: boolean;
}>`
  display: grid;
  grid-template-columns: auto auto;
  grid-column-gap: 10px;
  ${Text} {
    grid-column-end: span 2;
  }
`;

const StyledInput = styled(Input)`
  align-self: center;
`;

export default GeneralSettings;
